/**
 * fetchHotspotColumns — fetch + parse + flatten the hotspots feed to a
 * structure-of-arrays on a shared worker (see hotspotFetchWorker.ts). Falls back
 * to a main-thread fetch + flatten if workers are unavailable (SSR/jsdom/old
 * browser) or the worker crashes, so behaviour is never worse than before.
 */

import { flattenHotspots, type HotspotColumns } from './hotspotColumns';

interface WorkerResponse {
  id: number;
  ok: boolean;
  columns?: HotspotColumns;
  error?: string;
}

type Pending = { resolve: (value: HotspotColumns) => void; reject: (err: Error) => void };

let worker: Worker | null = null;
let workerUnavailable = false;
let nextId = 1;
const pending = new Map<number, Pending>();

function getWorker(): Worker | null {
  if (workerUnavailable) return null;
  if (worker) return worker;
  if (typeof Worker === 'undefined') {
    workerUnavailable = true;
    return null;
  }
  try {
    worker = new Worker(new URL('./hotspotFetchWorker.ts', import.meta.url), { type: 'module' });
    worker.onmessage = (e: MessageEvent<WorkerResponse>) => {
      const { id, ok, columns, error } = e.data;
      const p = pending.get(id);
      if (!p) return;
      pending.delete(id);
      if (ok && columns) p.resolve(columns);
      else p.reject(new Error(error || 'hotspot worker failed'));
    };
    worker.onerror = () => {
      workerUnavailable = true;
      for (const [, p] of pending) p.reject(new Error('hotspot worker error'));
      pending.clear();
      try { worker?.terminate(); } catch { /* ignore */ }
      worker = null;
    };
    return worker;
  } catch {
    workerUnavailable = true;
    return null;
  }
}

async function mainThreadFallback(url: string, headers?: Record<string, string>): Promise<HotspotColumns> {
  const response = await fetch(url, headers ? { headers } : undefined);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const geojson = await response.json();
  return flattenHotspots(geojson).columns;
}

/** Fetch the hotspots feed and return it as flattened columns. */
export function fetchHotspotColumns(url: string, headers?: Record<string, string>): Promise<HotspotColumns> {
  const w = getWorker();
  if (!w) return mainThreadFallback(url, headers);

  const id = nextId++;
  return new Promise<HotspotColumns>((resolve, reject) => {
    pending.set(id, { resolve, reject });
    try {
      w.postMessage({ id, url, headers });
    } catch {
      pending.delete(id);
      mainThreadFallback(url, headers).then(resolve, reject);
    }
  });
}
